import React from 'react';
import { CheckCircle, TrendingUp } from 'lucide-react';
import { BATCH_PRICING, BATCH_TIERS } from '@/config/pricing';
import { logger } from '@/utils/logger';

interface ProcessingTypeOption {
  type: string;
  price: number;
  title: string;
  description: string;
  features: string[];
  popular: boolean;
}

interface BatchProcessingTypeCardProps {
  selectedType: string;
  onTypeSelect: (type: string) => void;
  processableRows: number;
  aiOverviewEnabled: boolean;
  onAiOverviewToggle: (enabled: boolean) => void;
}

const AI_OVERVIEW_RATE = 0.01;

const isAiOverviewEligible = (type: string): boolean =>
  type === 'scoring' || type === 'scoring_enrich';

const getProcessingTypeCost = (
  type: string,
  rowCount: number,
  includeAiOverview = false
): number => {
  // Determine tier based on row count
  const tier =
    rowCount < BATCH_TIERS.TIER2_MIN
      ? 'tier1'
      : rowCount < BATCH_TIERS.TIER3_MIN
      ? 'tier2'
      : 'tier3';

  // Get base rate from BATCH_PRICING config
  const typeMap: Record<string, keyof typeof BATCH_PRICING> = {
    validate: 'validate',
    enrich: 'enrich',
    enrich_validate: 'enrich_validate',
    scoring: 'scoring',
    scoring_enrich: 'scoring_enrich',
  };

  const pricingKey = typeMap[type];
  let rate = pricingKey ? BATCH_PRICING[pricingKey][tier] : 0.15;

  if (includeAiOverview && isAiOverviewEligible(type)) {
    rate += AI_OVERVIEW_RATE;
  }

  return rate;
};

export const BatchProcessingTypeCard: React.FC<BatchProcessingTypeCardProps> = ({
  selectedType,
  onTypeSelect,
  processableRows,
  aiOverviewEnabled,
  onAiOverviewToggle,
}) => {
  const processingTypes: ProcessingTypeOption[] = [
    {
      type: 'validate',
      price: 0.15,
      title: 'Validate',
      description: 'Identity verification and fraud detection',
      features: ['Identity matching', 'Contact validation', 'Fraud risk scoring'],
      popular: false,
    },
    {
      type: 'enrich',
      price: 0.08,
      title: 'Enrich',
      description: 'Contact enhancement and data completion',
      features: ['Address completion', 'Additional contacts', 'Social matching'],
      popular: false,
    },
    {
      type: 'enrich_validate',
      price: 0.2,
      title: 'Enrich + Validate',
      description: 'Contact enrichment with identity verification',
      features: [
        'Complete enrichment',
        'Identity validation',
        'Comprehensive verification',
      ],
      popular: true,
    },
    {
      type: 'scoring',
      price: 0.35,
      title: 'Scoring',
      description: 'AI-powered collectability scoring',
      features: [
        'Recovery probability',
        'Settlement amounts',
        'Risk categorization',
      ],
      popular: false,
    },
    {
      type: 'scoring_enrich',
      price: 0.5,
      title: 'Scoring + Enrich',
      description: 'Complete scoring with data enrichment',
      features: ['AI scoring', 'Data enrichment', 'Maximum insights'],
      popular: false,
    },
  ];

  const handleTypeSelect = (type: string) => {
    const eligibleForAi = isAiOverviewEligible(type);

    if (!eligibleForAi && aiOverviewEnabled) {
      onAiOverviewToggle(false);
    }

    const price = getProcessingTypeCost(
      type,
      processableRows,
      eligibleForAi && aiOverviewEnabled
    );

    logger.info('Processing type selected', {
      type,
      pricePerRecord: price,
      aiOverviewEnabled: eligibleForAi && aiOverviewEnabled,
      processableRows,
      estimatedCost: (price * processableRows).toFixed(2),
    });

    onTypeSelect(type);
  };

  const handleAiToggle = (enabled: boolean) => {
    if (!isAiOverviewEligible(selectedType)) {
      return;
    }

    logger.info('AI overview preference updated', {
      enabled,
      processingType: selectedType,
      surchargePerRecord: enabled ? AI_OVERVIEW_RATE : 0,
    });

    onAiOverviewToggle(enabled);
  };

  return (
    <div className="mb-6">
      <h3 className="h2 mb-3">Select Processing Type</h3>

      {/* Basic Operations - 3 cards in a row */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        {processingTypes.slice(0, 3).map((type) => {
          const isSelected = selectedType === type.type;
          const includeAi =
            isSelected && aiOverviewEnabled && isAiOverviewEligible(type.type);
          const pricePerRecord = getProcessingTypeCost(
            type.type,
            processableRows,
            includeAi
          );

          return (
            <div
              key={type.type}
              className={`ca-card ca-card-p relative cursor-pointer transition-colors ${
                isSelected
                  ? 'border-blue-500 bg-blue-50'
                  : 'hover:border-slate-300'
              }`}
              onClick={() => handleTypeSelect(type.type)}
            >
              {type.popular && (
                <div className="absolute left-4 -top-2 rounded-full border border-blue-500 bg-white px-2 py-1 text-xs font-medium text-slate-600">
                  Popular
                </div>
              )}
              <div className="mb-2 flex items-center justify-between">
                <div className="font-medium text-slate-900">{type.title}</div>
                <div className="text-right">
                  <div className="font-semibold text-slate-900">
                    ${pricePerRecord.toFixed(2)}
                  </div>
                  <div className="text-xs text-slate-500">per record</div>
                </div>
              </div>
              <p className="mb-2 text-sm text-slate-600">{type.description}</p>
              <div className="space-y-1">
                {type.features.map((feature, index) => (
                  <div
                    key={index}
                    className="flex items-center text-xs text-slate-500"
                  >
                    <CheckCircle className="mr-1 h-3 w-3 text-slate-400" />
                    {feature}
                  </div>
                ))}
              </div>
              <div className="mt-3 border-t border-slate-200 pt-3">
                <div className="text-sm font-medium text-slate-700">
                  Est. cost: ${(pricePerRecord * processableRows).toFixed(2)}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* CollectABILITY Premium Products - 2 wider cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {processingTypes.slice(3, 5).map((type) => {
          const isSelected = selectedType === type.type;
          const includeAi =
            isSelected && aiOverviewEnabled && isAiOverviewEligible(type.type);
          const pricePerRecord = getProcessingTypeCost(
            type.type,
            processableRows,
            includeAi
          );

          return (
            <div
              key={type.type}
              className={`ca-card ca-card-p relative cursor-pointer transition-colors ${
                isSelected
                  ? 'border-blue-500 bg-blue-50'
                  : 'hover:border-slate-300'
              }`}
              onClick={() => handleTypeSelect(type.type)}
            >
              {type.popular && (
                <div className="absolute left-4 -top-2 rounded-full border border-blue-500 bg-white px-2 py-1 text-xs font-medium text-slate-600">
                  Popular
                </div>
              )}
              <div className="mb-2 flex items-center justify-between">
                <div className="font-medium text-slate-900">{type.title}</div>
                <div className="text-right">
                  <div className="font-semibold text-slate-900">
                    ${pricePerRecord.toFixed(2)}
                  </div>
                  <div className="text-xs text-slate-500">per record</div>
                </div>
              </div>
              <p className="mb-2 text-sm text-slate-600">{type.description}</p>
              <div className="space-y-1">
                {type.features.map((feature, index) => (
                  <div
                    key={index}
                    className="flex items-center text-xs text-slate-500"
                  >
                    <CheckCircle className="mr-1 h-3 w-3 text-slate-400" />
                    {feature}
                  </div>
                ))}
              </div>
              <div className="mt-3 border-t border-slate-200 pt-3">
                <div className="text-sm font-medium text-slate-700">
                  Est. cost: ${(pricePerRecord * processableRows).toFixed(2)}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* AI Overview Toggle - Only for Scoring Types */}
      {isAiOverviewEligible(selectedType) && (
        <div className="mt-4 ca-card ca-card-p flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="flex items-center gap-2 text-sm font-semibold text-slate-900">
              <TrendingUp className="h-4 w-4 text-slate-600" />
              AI Overview add-on
            </p>
            <p className="mt-1 text-xs text-slate-600">
              Generate an AI-powered recovery overview and strategy. Adds $
              {AI_OVERVIEW_RATE.toFixed(2)} per successful result when enabled.
            </p>
          </div>
          <label className="inline-flex items-center gap-3 cursor-pointer">
            <span className="text-xs font-medium text-slate-600">Off</span>
            <div
              className={`relative h-6 w-11 rounded-full transition-colors ${
                aiOverviewEnabled ? 'bg-blue-600' : 'bg-slate-300'
              }`}
            >
              <input
                type="checkbox"
                className="sr-only"
                checked={aiOverviewEnabled}
                onChange={(event) => handleAiToggle(event.target.checked)}
              />
              <span
                className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform ${
                  aiOverviewEnabled ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </div>
            <span className="text-xs font-medium text-slate-600">On</span>
          </label>
        </div>
      )}
    </div>
  );
};
